import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * The current user's role in a project. `null` means *not yet known* — the state for the whole
 * window between asking for a project and the server answering — and is deliberately distinct
 * from "known to be a viewer".
 */
export type ProjectRole = 'admin' | 'member' | 'viewer' | null;

/** Current user's membership data for a project (response from GET /api/projects/{id}/my-membership/). */
export interface ProjectMembershipData {
  id: string;
  project: string;
  status: string;
  /** Unified project role: 'admin' | 'member' | 'viewer'. */
  project_role: 'admin' | 'member' | 'viewer';
  created_at: string;
  updated_at: string;
}

/** API response wrapper for GET /api/projects/{project_id}/my-membership/. */
export interface MyMembershipResponseDto {
  status: string;
  message: string;
  data: ProjectMembershipData;
  errors: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root'
})
export class ProjectMembershipService {
  private readonly apiUrl = `${environment.apiHost}/api`;

  private readonly membershipSubject = new BehaviorSubject<ProjectMembershipData | null>(null);
  /** Last projectId for which membership was loaded (prevents redundant API calls). */
  private lastFetchedProjectId: string | null = null;
  /** In-flight request, shared so concurrent callers for the same project don't race into duplicate HTTP calls. */
  private pendingRequest: Observable<ProjectMembershipData | null> | null = null;
  private pendingProjectId: string | null = null;

  public membership$ = this.membershipSubject.asObservable();

  constructor(private readonly http: HttpClient) {}

  /** Returns current membership (null if not loaded or 404). */
  get membership(): ProjectMembershipData | null {
    return this.membershipSubject.value;
  }

  /**
   * Unified project role of the current user, or `null` while it is not yet known.
   *
   * Validated rather than trusted: a value the app does not recognise — a casing change, a role
   * added server-side — reads as *not yet known* instead of slipping into the union, where
   * `roleCanEditContent` would quietly file it under "viewer" and call that an answer.
   */
  get projectRole(): ProjectRole {
    return toProjectRole(this.membershipSubject.value?.project_role);
  }

  /**
   * True if the current user may create and edit content in the project — everything a viewer
   * may not do. This is the positive form of the old `!isProjectViewer` checks: negating "is a
   * viewer" answered `true` for a role nobody had looked up yet, which handed the whole boot
   * window an editor's UI.
   */
  get canEditContent(): boolean {
    return roleCanEditContent(this.projectRole);
  }

  /** True if the current user may delete a site (role = admin). */
  get canDeleteSite(): boolean {
    return this.projectRole === 'admin';
  }

  /** True if the current user can invite members to the project (role = admin). */
  get canInvite(): boolean {
    return this.projectRole === 'admin';
  }

  /** True if the current user can edit the project settings (role = admin). */
  get canEditProject(): boolean {
    return this.projectRole === 'admin';
  }

  /** True if the current user can delete the project (role = admin). */
  get canDeleteProject(): boolean {
    return this.projectRole === 'admin';
  }

  /**
   * True if the current user (admin) can remove the given member.
   * Admins can remove members and viewers, but not other admins.
   */
  canRemoveMember(member: { project_role?: string }): boolean {
    if (this.projectRole !== 'admin') return false;
    return member.project_role !== 'admin';
  }

  /**
   * True if the current user can invite someone as admin.
   * Only project admins can invite with any role (including admin).
   */
  get canInviteAsAdmin(): boolean {
    return this.projectRole === 'admin';
  }

  /**
   * Loads the current user's membership for the given project.
   * Skips the request if projectId matches the last fetched one.
   * Emits null when the user is not an active member (404 or error).
   */
  loadForProject(projectId: string): Observable<ProjectMembershipData | null> {
    if (!projectId) {
      this.lastFetchedProjectId = null;
      this.pendingRequest = null;
      this.pendingProjectId = null;
      this.membershipSubject.next(null);
      return of(null);
    }

    if (this.lastFetchedProjectId === projectId) {
      return of(this.membershipSubject.value);
    }

    // Two callers (e.g. the layout and a page component) can request the same project's
    // membership before either HTTP response resolves — share the in-flight request instead
    // of racing into two real GET calls.
    if (this.pendingProjectId === projectId && this.pendingRequest) {
      return this.pendingRequest;
    }

    // Past both early returns, so whatever is held belongs to a project we are leaving. Drop it
    // before the request goes out: keeping it would answer role questions about the new project
    // with the old project's role for a whole round trip.
    this.membershipSubject.next(null);

    this.pendingProjectId = projectId;
    this.pendingRequest = this.http
      .get<MyMembershipResponseDto>(`${this.apiUrl}/projects/${projectId}/my-membership/`)
      .pipe(
        map((res) => res?.data ?? null),
        tap((data) => {
          if (data) {
            this.lastFetchedProjectId = projectId;
            this.membershipSubject.next(data);
          }
        }),
        catchError((error: unknown) => {
          // "You are not a member of this project" is an answer, and worth remembering. A 500 or
          // a dropped connection is not: caching it would leave an admin holding no role in their
          // own project for the rest of the session, with no path back — every later call short-
          // circuits on `lastFetchedProjectId`. Leave it unset so the next caller retries.
          if (isMembershipDenied(error)) {
            this.lastFetchedProjectId = projectId;
          }
          this.membershipSubject.next(null);
          return of(null);
        }),
        finalize(() => {
          this.pendingRequest = null;
          this.pendingProjectId = null;
        }),
        shareReplay(1)
      );

    return this.pendingRequest;
  }

  /**
   * Forces a reload of membership for the given project on the next loadForProject call
   * (e.g. after a role change).
   */
  clearCacheForProject(projectId: string): void {
    if (this.lastFetchedProjectId === projectId) {
      this.lastFetchedProjectId = null;
    }
    if (this.pendingProjectId === projectId) {
      this.pendingRequest = null;
      this.pendingProjectId = null;
    }
  }

  /** Clears membership state (e.g. when navigating away from projects). */
  clear(): void {
    this.lastFetchedProjectId = null;
    this.pendingRequest = null;
    this.pendingProjectId = null;
    this.membershipSubject.next(null);
  }
}

/**
 * Whether a role may create and edit content in a project. Fails closed on `null`: a role that
 * has not arrived yet is not an editor.
 *
 * Exported as a function because the projects list carries a role per row
 * (`ProjectsDataDto.user_role`), fresh from the server and belonging to a project other than the
 * active one, so those rows cannot ask this service — and must not answer the question a second,
 * differently-worded way.
 */
export function roleCanEditContent(role: ProjectRole): boolean {
  return role === 'admin' || role === 'member';
}

/**
 * The server has answered the question: this user holds no membership here. Anything else — 5xx,
 * a timeout, an offline tab — means the question is still open.
 */
function isMembershipDenied(error: unknown): boolean {
  return error instanceof HttpErrorResponse && (error.status === 404 || error.status === 403);
}

/** Anything the server did not send as a role this app knows reads as *not yet known*. */
export function toProjectRole(role: string | null | undefined): ProjectRole {
  return role === 'admin' || role === 'member' || role === 'viewer' ? role : null;
}
